import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Beat, Idea } from '../api'
import { CloseIcon, PlusIcon, ScreenIcon } from '../components/icons'
import {
  beatsOf,
  clock,
  EXTRA_BEAT,
  isBlank,
  isCardBeat,
  PROMPTS,
  SHORT_LIMIT_SECONDS,
  timing,
} from '../domain/script'
import { isPending, updateIdea } from '../state/ideas'
import { toast } from '../state/toast'

/**
 * The script, beat by beat: what Martin says, and under it what is on screen.
 *
 * Until something is written the beats are the format's template, and
 * nothing is stored. Typing is local; leaving a field saves the whole script,
 * the way the title and the notes save, so the activity log gets one line per
 * sitting rather than one per keystroke. Adding or removing a beat saves at
 * once. The running time is an estimate at a talking pace, and on a Short it
 * is measured against the minute a Short should stay under.
 */
export function ScriptEditor({ idea }: { idea: Idea }) {
  const [draft, setDraft] = useState<Beat[]>(() => beatsOf(idea))
  const [editing, setEditing] = useState(false)
  const section = useRef<HTMLElement>(null)
  const focusLast = useRef(false)

  // Follow the server (or an undo, or a format change) while nobody is typing.
  useEffect(() => {
    if (!editing) setDraft(beatsOf(idea))
  }, [idea.script, idea.format, editing])

  const save = (beats: Beat[]) => {
    const next = isBlank(beats) && beats.length === beatsOf({ ...idea, script: [] }).length ? [] : beats
    if (JSON.stringify(next) !== JSON.stringify(idea.script)) void updateIdea(idea.id, { script: next })
  }

  // Closing the panel or leaving the page mid-sentence still keeps the sentence.
  const latest = useRef({ draft, editing, save })
  latest.current = { draft, editing, save }
  useEffect(
    () => () => {
      if (latest.current.editing) latest.current.save(latest.current.draft)
    },
    [],
  )

  useLayoutEffect(() => {
    if (!focusLast.current) return
    focusLast.current = false
    const fields = section.current?.querySelectorAll<HTMLTextAreaElement>('.beat-say')
    fields?.[fields.length - 1]?.focus()
  })

  const change = (index: number, field: 'say' | 'screen', value: string) =>
    setDraft((beats) => beats.map((beat, n) => (n === index ? { ...beat, [field]: value } : beat)))

  const add = () => {
    const next = [...draft, { kind: EXTRA_BEAT, say: '', screen: '' }]
    setDraft(next)
    save(next)
    focusLast.current = true
  }

  const remove = (index: number) => {
    const gone = draft[index]
    const next = draft.filter((_, n) => n !== index)
    setDraft(next)
    save(next)
    if (gone.say.trim() || gone.screen.trim()) {
      toast(`${gone.kind} removed`, {
        action: {
          label: 'Undo',
          run: () => void updateIdea(idea.id, { script: [...next.slice(0, index), gone, ...next.slice(index)] }),
        },
      })
    }
  }

  const { starts, total } = timing(draft, idea.title)
  const onShorts = idea.channels.includes('short')
  const over = onShorts && total > SHORT_LIMIT_SECONDS

  return (
    <section
      ref={section}
      className="script"
      aria-label="Script"
      onFocus={() => setEditing(true)}
      onBlur={(event) => {
        if (section.current?.contains(event.relatedTarget as Node | null)) return
        setEditing(false)
        save(draft)
      }}
    >
      <div className="detail-section-head">
        <h2 className="detail-section-title">Script</h2>
        {total >= 1 && (
          <span className="script-runtime mono" data-over={over || undefined}>
            ~{Math.round(total)} s{over && ', over a minute'}
          </span>
        )}
        {onShorts && total >= 1 && (
          <span className="script-meter" data-over={over || undefined} title="A Short should stay under a minute">
            <span style={{ width: `${Math.min(100, (total / SHORT_LIMIT_SECONDS) * 100)}%` }} />
          </span>
        )}
      </div>

      <ol className="beats">
        {draft.map((beat, index) => {
          const card = isCardBeat(beat, index)
          return (
            <li key={index} className="beat">
              <div className="beat-label">
                <span className="beat-kind">{beat.kind}</span>
                <span className="beat-time mono">{clock(starts[index])}</span>
              </div>
              <div className="beat-body">
                {card ? (
                  <p className="beat-say beat-card">
                    {idea.title}
                    <span className="beat-card-hint">Read off the card</span>
                  </p>
                ) : (
                  <BeatField
                    className="beat-say"
                    label={`${beat.kind}: what you say`}
                    value={beat.say}
                    placeholder={PROMPTS[beat.kind] ?? 'What do you say here?'}
                    disabled={isPending(idea)}
                    onChange={(value) => change(index, 'say', value)}
                  />
                )}
                <div className="beat-screen">
                  <ScreenIcon size={15} />
                  <BeatField
                    className="beat-screen-field"
                    label={`${beat.kind}: on screen`}
                    value={beat.screen}
                    placeholder={card ? 'On screen: the card' : 'On screen: a card, a cutaway, or just you'}
                    disabled={isPending(idea)}
                    onChange={(value) => change(index, 'screen', value)}
                  />
                </div>
              </div>
              {!card && (
                <button
                  type="button"
                  className="beat-remove"
                  aria-label={`Remove ${beat.kind}`}
                  disabled={isPending(idea)}
                  onClick={() => remove(index)}
                >
                  <CloseIcon size={16} />
                </button>
              )}
            </li>
          )
        })}
      </ol>
      <button type="button" className="beat-add" disabled={isPending(idea)} onClick={add}>
        <PlusIcon size={16} />
        Add a beat
      </button>
    </section>
  )
}

function BeatField({
  className,
  label,
  value,
  placeholder,
  disabled,
  onChange,
}: {
  className: string
  label: string
  value: string
  placeholder: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
  }, [value])
  return (
    <textarea
      ref={ref}
      className={className}
      aria-label={label}
      rows={1}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' || (event.key === 'Enter' && (event.metaKey || event.ctrlKey))) {
          event.currentTarget.blur()
        }
      }}
    />
  )
}
